import Papa from "papaparse";

import { Maybe, SystemOfMeasurement } from "fond/types";
import { Currency } from "fond/types/currency";
import { LayerConfig } from "fond/types/ProjectLayerConfig";
import {
  CoreConfigurationLayerMapping,
  CoreConfigurationRequest,
  CoreConfigurationSpliceConfiguration,
  ScenarioConfiguration,
  VersionCoreConfiguration,
  VersionScenarioConfiguration,
} from "fond/types/version";
import { ExpectedFileMatch, fuzzyMatchFile } from "fond/utils/fuzzyFileMatch";
import { safeJSONParse } from "fond/utils/json";

import { c2sFileMap } from "./files";
import { FormData } from "./SetupForm";

export const defaultFormValues: FormData = {
  Files: {
    Occupancy: null,
    Artifacts: [],
  },
  DefaultBins: false,
  ToolConfigurations: {
    CostToServe: {
      CostingLayer: null,
      BinCount: 5,
      AdditionalSettings: `{
    "root_node_filter": [
        "all",
        ["==", ["prop", ["elementData"], "Tier"], "T3"],
        ["==", ["prop", ["elementData"], "Type"], "HUB"]
    ]
}`,
    },
    DesignPhasePartitioner: {
      AdditionalSettings: `{
    "root_node_filter": [
        "all",
        ["==", ["prop", ["elementData"], "Tier"], "T3"],
        ["==", ["prop", ["elementData"], "Type"], "HUB"]
    ]
}`,
    },
  },
  PathLayer: null,
  ScenarioConfigInput: {
    BillOfMaterials: {
      Type: "file",
      Data: null,
    },
    CostBreakdown: {
      Type: "file",
      Data: null,
    },
    Occupancy: {
      Type: "layer",
      Layer: { LayerKey: null },
    },
  },
  CoreConfigInput: {
    LayersConfiguration: {
      Type: "file",
      Data: null,
    },
    LayerMappings: [],
    SpliceConfiguration: null,
    SpliceTables: [],
  },
  Currency: "USD",
  SystemOfMeasurement: "imperial",
};

export const ignoreLayerOption = {
  Type: "LAYER",
  ID: undefined,
  Key: undefined,
  Label: "Ignore Layer",
} as unknown as LayerConfig;

/**
 * File type identifiers for C2S artifacts
 */
export type C2SArtifactFileType = "layersConfiguration" | "costBreakdown" | "bom" | "spliceTables";

/**
 * File types that accept a list of files rather than a single file (e.g. splice tables)
 */
export const MULTI_MATCH_ARTIFACT_TYPES: ReadonlySet<C2SArtifactFileType> = new Set(["spliceTables"]);

const KEYWORDS_BY_ID: Record<C2SArtifactFileType, string[]> = {
  bom: ["bom", "bill", "materials", "bill_of_materials", "billofmaterials"],
  costBreakdown: ["cost", "breakdown", "cost_breakdown", "costbreakdown"],
  layersConfiguration: ["layers", "configuration", "config", "layers_configuration", "layersconfiguration"],
  spliceTables: ["splice", "splices", "splice_table", "splicetable", "splice_tables", "splicetables"],
};

const EXPECTED_FILES: Array<ExpectedFileMatch<C2SArtifactFileType>> = c2sFileMap.ArtifactFiles.map((fileConfig) => {
  const lastDotIndex = fileConfig.file.lastIndexOf(".");
  const id = fileConfig.id as C2SArtifactFileType;
  return {
    id,
    baseName: fileConfig.file.slice(0, lastDotIndex),
    keywords: KEYWORDS_BY_ID[id],
    allowedExtensions: [fileConfig.file.slice(lastDotIndex)],
  };
});

export const matchC2SArtifactFile = (uploadedFileName: string) => fuzzyMatchFile(uploadedFileName, EXPECTED_FILES);

/**
 * Determine whether the collaboration CTS "Setup" step is complete.
 *
 * - Core configuration is always required
 * - Then if the collaboration feature flag is:
 *    - On, require either the scenario configuration or the splice configuration to be present.
 *    - Off, require the scenario configuration to be present.
 */
export const isSetupComplete = (
  coreConfiguration: Maybe<VersionCoreConfiguration>,
  scenarioConfiguration: Maybe<VersionScenarioConfiguration>,
  isSpliceViewEnabled: boolean
): boolean => {
  if (!coreConfiguration) return false;
  if (isSpliceViewEnabled) return !!scenarioConfiguration || !!coreConfiguration.SpliceConfiguration;
  return !!scenarioConfiguration;
};

const COST_PER_PASSING_AVERAGE_MULTIPLIER = 5;

export interface BudgetSliderConfig {
  max: number;
  defaultValue: number;
}

/**
 * Calculates the CFO budget maximums and default values.
 *
 * The default values are:
 * - The monetary budget is set to half the total cost, and
 * - The cost-per-passing budget is set to the average cost per passing.
 */
export const calculateBudgets = (
  bomTotal: number,
  serviceLocationCount: number | undefined
): { monetary: BudgetSliderConfig; costPerPassing: BudgetSliderConfig | undefined } => {
  const monetaryMax = Math.ceil(bomTotal);
  const average = serviceLocationCount ? bomTotal / serviceLocationCount : undefined;

  return {
    monetary: { max: monetaryMax, defaultValue: Math.round(monetaryMax / 2) },
    // There is no global upper bound on the allowable costPerPassing: allowing more budget than the average
    // cost-per-passing is reasonable _if_ the monetary budget is reduced. The COST_PER_PASSING_AVERAGE_MULTIPLIER here
    // is a fudge factor that is supposed to give the user a 'reasonable' range of choices.
    costPerPassing:
      average === undefined ? undefined : { max: Math.ceil(average * COST_PER_PASSING_AVERAGE_MULTIPLIER), defaultValue: Math.round(average) },
  };
};

export interface EffectiveBudget {
  value: number;
  limit: number;
}

/**
 * Calculate the effective budgets.
 *
 * The monetary and cost-per-passing budgets are interwoven and influence each other.
 */
export const calculateEffectiveBudgets = ({
  monetary,
  costPerPassing,
  isMonetaryBudgetEnabled,
  isCostPerPassingBudgetEnabled,
  monetaryBudget,
  costPerPassingBudget,
  serviceLocationCount,
}: {
  monetary: BudgetSliderConfig;
  costPerPassing: BudgetSliderConfig;
  isMonetaryBudgetEnabled: boolean;
  isCostPerPassingBudgetEnabled: boolean;
  monetaryBudget: number;
  costPerPassingBudget: number;
  serviceLocationCount: number;
}): { monetary: EffectiveBudget; costPerPassing: EffectiveBudget } => {
  // If the monetary budget is not enabled, then the cost-per-passing budget achieves nothing when it exceeds
  // the default value (the current average cost-per-passing).
  const costPerPassingLimit = isMonetaryBudgetEnabled && monetaryBudget < monetary.max ? costPerPassing.max : costPerPassing.defaultValue;
  const effectiveCostPerPassing = Math.min(costPerPassingBudget, costPerPassingLimit);

  // If a cost-per-passing budget is enabled, then the monetary budget is limited by the product
  // of this budget and the service location count.
  const monetaryLimit = isCostPerPassingBudgetEnabled
    ? Math.min(monetary.max, Math.ceil(effectiveCostPerPassing * serviceLocationCount))
    : monetary.max;

  return {
    monetary: { value: Math.min(monetaryBudget, monetaryLimit), limit: monetaryLimit },
    costPerPassing: { value: effectiveCostPerPassing, limit: costPerPassingLimit },
  };
};

/**
 * Return the step size to use for a slider with a given maximum value.
 *
 * The step size is chosen to give roughly 100 increments across the slider range.
 */
export const calculateSliderStep = (max: number): number => {
  const target = max / 100;
  if (target <= 1) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(target));
  return [5, 2, 1].map((factor) => factor * magnitude).find((step) => step <= target) ?? 1;
};

/**
 * Return whether the enabled budgets could theoretically restrict the build scope.
 */
export const isBudgetConfigRestrictive = ({
  bomTotal,
  averageCostPerPassing,
  monetaryBudget,
  costPerPassingBudget,
}: {
  bomTotal: number;
  averageCostPerPassing: number;
  monetaryBudget: number | null;
  costPerPassingBudget: number | null;
}): boolean =>
  (monetaryBudget !== null && monetaryBudget < bomTotal) || (costPerPassingBudget !== null && costPerPassingBudget < averageCostPerPassing);

export interface C2SLayersConfiguration {
  physical_elements?: string[] | null;
  network_elements?: string[] | null;
  edge_elements?: string[] | null;
  node_elements?: string[] | null;
  layer_name_mapping?: Record<string, string> | null;
  combine_layers?: Record<string, string[]> | null;
  fond_id_mapping?: Record<string, string> | null;
  start_end_id_mapping?: Record<string, { StartID: string; EndID: string }> | null;
  splice_elements?: {
    column_names?: {
      closure?: string | null;
      cable_in?: string | null;
      fibre_in?: string | null;
      tier_in?: string | null;
      cable_out?: string | null;
      fibre_out?: string | null;
      tier_out?: string | null;
      type?: string | null;
    } | null;
  } | null;
}

export const resolveLayerConfigurationNameFromFilename = (layersConfiguration: C2SLayersConfiguration, fileName: string): string => {
  return layersConfiguration?.layer_name_mapping?.[fileName] || fileName;
};

/**
 * Resolves the name a layer is known by within `layersConfiguration` (e.g. in `physical_elements`,
 * `fond_id_mapping`, etc.), given the original name of the uploaded layer file.
 *
 * For example, given the following configuration:
 * ```
 * layer_name_mapping: { pole: "Pole", span: "Span", duct: "Duct" }
 * combine_layers: { FiberCable: ["Span", "Duct"] }
 * ```
 * - `resolveConfigLayerName(config, "hub")` -> `"hub"` (no rename or combine, so it's unchanged)
 * - `resolveConfigLayerName(config, "pole")` -> `"Pole"` (renamed to "Pole", which isn't in any combine_layers group)
 * - `resolveConfigLayerName(config, "span")` -> `"FiberCable"` (renamed to "Span", which is combined into "FiberCable")
 */
export const resolveConfigLayerName = (layersConfiguration: C2SLayersConfiguration, originalName: string): string => {
  const renamedName = resolveLayerConfigurationNameFromFilename(layersConfiguration, originalName);

  const combinedEntry = Object.entries(layersConfiguration?.combine_layers ?? {}).find(([, members]) => members.includes(renamedName));

  return combinedEntry?.[0] ?? renamedName;
};

/**
 * Derives the per-layer attributes (element type and ID field names) for a layer from `layersConfiguration`,
 * so an uploaded layers_configuration.json can pre-populate the layer mapping form instead of requiring the
 * user to set every attribute manually.
 */
export const deriveLayerMappingAttributes = (
  layersConfiguration: C2SLayersConfiguration,
  originalName: string
): Pick<
  CoreConfigurationLayerMapping,
  "FondIDAttribute" | "StartIDAttribute" | "EndIDAttribute" | "NetworkElement" | "PhysicalElement" | "NodeElement" | "EdgeElement"
> => {
  // A layer can be referenced by either its renamed name or its combined name.
  const layerNames = [
    resolveLayerConfigurationNameFromFilename(layersConfiguration, originalName),
    resolveConfigLayerName(layersConfiguration, originalName),
  ];

  // Helper function to find the first listed name in a given list of elements.
  const findListedName = (elements?: string[] | null) => layerNames.find((name) => elements?.includes(name));

  // Start/End ID attributes are only derived for edge elements.
  const edgeName = findListedName(layersConfiguration?.edge_elements);
  const startEndIDs = edgeName ? layersConfiguration?.start_end_id_mapping?.[edgeName] : undefined;

  return {
    FondIDAttribute: layerNames.map((name) => layersConfiguration?.fond_id_mapping?.[name]).find((field) => field != null) ?? null,
    StartIDAttribute: startEndIDs?.StartID ?? null,
    EndIDAttribute: startEndIDs?.EndID ?? null,
    NetworkElement: !!findListedName(layersConfiguration?.network_elements),
    PhysicalElement: !!findListedName(layersConfiguration?.physical_elements),
    NodeElement: !!findListedName(layersConfiguration?.node_elements),
    EdgeElement: !!edgeName,
  };
};

/**
 * Derives the splice CSV column mapping from `layersConfiguration.splice_elements.column_names`.
 */
export const deriveSpliceConfigurationDefaults = (layersConfiguration: C2SLayersConfiguration): CoreConfigurationSpliceConfiguration => {
  const columnNames = layersConfiguration?.splice_elements?.column_names;

  return {
    ClosureColumn: columnNames?.closure ?? "",
    CableInColumn: columnNames?.cable_in ?? "",
    FibreInColumn: columnNames?.fibre_in ?? "",
    TierInColumn: columnNames?.tier_in ?? null,
    TypeColumn: columnNames?.type ?? null,
    CableOutColumn: columnNames?.cable_out ?? "",
    FibreOutColumn: columnNames?.fibre_out ?? "",
    TierOutColumn: columnNames?.tier_out ?? null,
  };
};

/**
 * Parses the header row of a CSV file's contents into a list of column names.
 */
export const parseCsvHeader = (csvText: string): string[] => {
  const { data } = Papa.parse<string[]>(csvText, {
    preview: 1, // Only parse the first row
    skipEmptyLines: true,
  });
  return (data[0] ?? []).map((column) => column.trim());
};

/**
 * Whether the form has the artifacts required to configure Cost to Serve. When either is missing, the step is treated
 * as skipped and the scenario configuration is not saved.
 */
export const hasCostToServeArtifacts = (values: FormData): boolean =>
  !!values.ScenarioConfigInput.BillOfMaterials?.Data && !!values.ScenarioConfigInput.CostBreakdown?.Data;

/**
 * Whether splice tables are available for the splice view, either newly uploaded to the form or already stored
 * against the version.
 */
export const hasSpliceTables = (values: FormData, hasExistingSpliceTables: boolean): boolean =>
  (values.CoreConfigInput.SpliceTables?.length ?? 0) > 0 || hasExistingSpliceTables;

const DEFAULT_ID_ATTRIBUTE_NAMES = {
  FondIDAttribute: "FondID",
  StartIDAttribute: "StartID",
  EndIDAttribute: "EndID",
} as const;

/**
 * Fills in any still-null ID attributes with an exact-named match from the mapped layer's available attributes
 * (e.g. "FondID", "StartID", "EndID"), since that's the common convention when the uploaded layers configuration
 * doesn't explicitly specify a mapping for this layer. Start/End ID attributes are only relevant for edges, so they are
 * only defaulted when the layer is an edge element.
 */
export const applyDefaultIdAttributes = <
  T extends Pick<CoreConfigurationLayerMapping, "FondIDAttribute" | "StartIDAttribute" | "EndIDAttribute" | "EdgeElement">,
>(
  attributes: T,
  availableAttributeNames: string[]
): T => {
  const result = { ...attributes };
  (Object.entries(DEFAULT_ID_ATTRIBUTE_NAMES) as Array<[keyof typeof DEFAULT_ID_ATTRIBUTE_NAMES, string]>).forEach(([field, defaultName]) => {
    const isApplicable = field === "FondIDAttribute" || result.EdgeElement;
    if (isApplicable && result[field] == null && availableAttributeNames.includes(defaultName)) {
      result[field] = defaultName;
    }
  });
  return result;
};

export const resolveLayerConfigurationInputLayers = (layersConfiguration: C2SLayersConfiguration): Set<string> => {
  let layers = new Set<string>();
  layersConfiguration?.physical_elements?.forEach((el) => layers.add(el));
  layersConfiguration?.network_elements?.forEach((el) => layers.add(el));
  layersConfiguration?.edge_elements?.forEach((el) => layers.add(el));
  layersConfiguration?.node_elements?.forEach((el) => layers.add(el));

  // The BOM processes layer configurations by (i) renaming, and then (ii) combining layers. We're trying
  // to do the reverse here, so we first split up combined layers and then revert back to the original name.
  if (layersConfiguration?.combine_layers) {
    layers = new Set(Array.from(layers).flatMap((layer) => layersConfiguration?.combine_layers?.[layer] ?? [layer]));
  }

  if (layersConfiguration?.layer_name_mapping) {
    const reverseNameMap = new Map<string, string>(
      Object.entries(layersConfiguration.layer_name_mapping).map(([original, renamed]) => [renamed, original])
    );
    layers = new Set(Array.from(layers).map((layer) => reverseNameMap.get(layer) ?? layer));
  }

  return layers;
};

/**
 * Filters out null/undefined/ignoreLayerOption values from a layer mapping.
 */
export const filterLayerMappings = (mapping: Record<string, LayerConfig | null>): Record<string, LayerConfig> => {
  return Object.fromEntries(Object.entries(mapping).filter(([_, value]) => value != null && value !== ignoreLayerOption)) as Record<
    string,
    LayerConfig
  >;
};

export const convertLayerMappings = (
  mapping: Record<string, LayerConfig | null>,
  layersConfiguration?: C2SLayersConfiguration
): FormData["CoreConfigInput"]["LayerMappings"] => {
  return Object.entries(filterLayerMappings(mapping)).map(([key, layerConfig]) => {
    const attributes = layersConfiguration
      ? deriveLayerMappingAttributes(layersConfiguration, key)
      : {
          FondIDAttribute: null,
          StartIDAttribute: null,
          EndIDAttribute: null,
          NetworkElement: false,
          PhysicalElement: false,
          NodeElement: false,
          EdgeElement: false,
        };

    return {
      Name: key,
      Layer: { LayerKey: layerConfig.Key },
      ...applyDefaultIdAttributes(attributes, layerConfig.Attributes?.map((attribute) => attribute.Name) ?? []),
    };
  });
};

const DEFAULT_ADDITIONAL_SETTINGS = `{
    "root_node_filter": [
        "all",
        ["==", ["prop", ["elementData"], "Tier"], "T3"],
        ["==", ["prop", ["elementData"], "Type"], "HUB"]
    ]
}`;

/**
 * Converts a VersionCoreConfiguration and/or VersionScenarioConfiguration to FormData format. Either may be
 * absent - e.g. Cost to Serve is an optional step, so a version can have a core configuration (layers, splice
 * mapping) without ever having a scenario configuration (Cost to Serve inputs) saved, and vice versa.
 * Specifically:
 * 1) Parses CoreConfiguration.LayersConfiguration.Data from JSON string to object
 * 2) Stringifies ToolConfig.CostToServe.AdditionalSettings from object to JSON string
 * 3) Stringifies ToolConfig.DesignPhasePartitioner.AdditionalSettings from object to JSON string
 * 4) Adds Files field for form compatibility
 */
export const convertConfigToFormData = (
  coreConfiguration: Maybe<VersionCoreConfiguration>,
  scenarioConfiguration: Maybe<VersionScenarioConfiguration>,
  systemOfMeasurement: SystemOfMeasurement = "imperial",
  currency: Currency = "USD"
): FormData => {
  return {
    Files: {
      Artifacts: [],
      Occupancy: null,
    },
    DefaultBins: scenarioConfiguration ? scenarioConfiguration.ToolConfigurations.CostToServe.BinCount == null : false,
    ScenarioConfigInput: scenarioConfiguration?.Inputs ?? {
      BillOfMaterials: { Type: "file", Data: null },
      CostBreakdown: { Type: "file", Data: null },
      Occupancy: { Type: "layer", Layer: { LayerKey: null } },
    },
    CoreConfigInput: {
      LayersConfiguration: {
        Type: "file",
        Data: safeJSONParse(coreConfiguration?.LayersConfiguration.Data || ""),
      },
      LayerMappings: coreConfiguration?.LayerMappings ?? [],
      SpliceConfiguration: coreConfiguration?.SpliceConfiguration ?? null,
      // The raw splice CSVs aren't returned by the core-configuration API.
      SpliceTables: [],
    },
    ToolConfigurations: scenarioConfiguration
      ? {
          ...scenarioConfiguration.ToolConfigurations,
          CostToServe: {
            ...scenarioConfiguration.ToolConfigurations.CostToServe,
            AdditionalSettings: JSON.stringify(scenarioConfiguration.ToolConfigurations.CostToServe.AdditionalSettings),
          },
          DesignPhasePartitioner: {
            ...scenarioConfiguration.ToolConfigurations.DesignPhasePartitioner,
            AdditionalSettings: JSON.stringify(scenarioConfiguration.ToolConfigurations.DesignPhasePartitioner.AdditionalSettings),
          },
        }
      : {
          CostToServe: { CostingLayer: null, BinCount: 5, AdditionalSettings: DEFAULT_ADDITIONAL_SETTINGS },
          DesignPhasePartitioner: { AdditionalSettings: DEFAULT_ADDITIONAL_SETTINGS },
        },
    PathLayer: scenarioConfiguration?.PathLayer ?? null,
    Currency: currency,
    SystemOfMeasurement: systemOfMeasurement,
  };
};

/**
 * Converts FormData to ScenarioConfiguration format.
 * Specifically:
 * 1) Parses ToolConfig.CostToServe.AdditionalSettings from JSON string to object
 * 2) Parses ToolConfig.DesignPhasePartitioner.AdditionalSettings from JSON string to object
 * 3) Removes the Files and CoreConfigInput fields (not part of ScenarioConfiguration)
 */
export const convertFormDataToScenarioConfig = (formData: FormData): ScenarioConfiguration => {
  return {
    Inputs: {
      BillOfMaterials: {
        Type: "file",
        Data: formData.ScenarioConfigInput.BillOfMaterials.Data ?? "",
      },
      CostBreakdown: {
        Type: "file",
        Data: formData.ScenarioConfigInput.CostBreakdown.Data ?? "",
      },
      Occupancy:
        formData.ScenarioConfigInput.Occupancy.Type === "layer"
          ? {
              Type: "layer",
              Layer: {
                LayerKey: formData.ScenarioConfigInput.Occupancy.Layer?.LayerKey ?? "",
              },
            }
          : {
              Type: "file",
              Data: formData.ScenarioConfigInput.Occupancy.Data ?? "",
            },
    },
    ToolConfigurations: {
      CostToServe: {
        CostingLayer: formData.ToolConfigurations.CostToServe.CostingLayer ?? { LayerKey: "" },
        BinCount: formData.DefaultBins ? null : formData.ToolConfigurations.CostToServe.BinCount,
        AdditionalSettings: safeJSONParse(formData.ToolConfigurations.CostToServe.AdditionalSettings),
      },
      DesignPhasePartitioner: {
        ...formData.ToolConfigurations.DesignPhasePartitioner,
        AdditionalSettings: safeJSONParse(formData.ToolConfigurations.DesignPhasePartitioner.AdditionalSettings),
      },
    },
    PathLayer: formData.PathLayer?.LayerKey ? { LayerKey: formData.PathLayer.LayerKey } : null,
  };
};

/**
 * Converts FormData to CoreConfiguration format.
 * Specifically, stringifies LayersConfiguration.Data from object to JSON string.
 */
export const convertFormDataToCoreConfig = (formData: FormData): CoreConfigurationRequest => {
  const layersConfigurationData = formData.CoreConfigInput.LayersConfiguration.Data;
  const { SpliceConfiguration, SpliceTables } = formData.CoreConfigInput;
  return {
    LayersConfiguration: {
      Type: "file",
      // JSON.stringify(null) returns the string "null", not null/undefined
      Data: layersConfigurationData ? JSON.stringify(layersConfigurationData) : "",
    },
    LayerMappings: formData.CoreConfigInput.LayerMappings,
    SpliceConfiguration,
    // Only send SpliceTables when new files were uploaded.
    SpliceTables: SpliceTables.length > 0 ? SpliceTables : undefined,
  };
};
